import type { FullUser } from '@/graphql/__generated/resolvers-types';
import { PAGE_TYPES } from '@/utils/constants/tracking';

import { buildAdobeUserTrackingFields } from './buildAdobeUserTrackingFields';

/** Profile-picker routes where subscriber fields may emit before activeProfile is set. */
const PROFILE_PICKER_PAGE_TYPES = new Set<string>([PAGE_TYPES.WHOS_WATCHING]);

const PROFILE_PICKER_PATH_PATTERN = /^\/(?:[a-z]{2}\/)?user-profile\/whos-watching\/?$/i;

export function isProfilePickerPageType(pageType?: string | null): boolean {
    return Boolean(pageType && PROFILE_PICKER_PAGE_TYPES.has(pageType));
}

export function isProfilePickerPathname(pathname?: string | null): boolean {
    return Boolean(pathname && PROFILE_PICKER_PATH_PATTERN.test(pathname));
}

/** pageType and URL must both match — pageType can lag behind client navigation. */
export function isProfilePickerRoute(pageType?: string | null, pathname?: string | null): boolean {
    return isProfilePickerPageType(pageType) && isProfilePickerPathname(pathname);
}

/**
 * Gate the first page-view / readDataLayerPageUpdate until UserFullProfile has settled.
 * Avoids firing trackState as ANON while the subscriber session is still loading.
 *
 * Do not use the derived `isLoggedIn` hook alone: before the query returns,
 * `userFullProfile` is the ANON_PROFILE placeholder (truthy) with no `isLoggedIn` field.
 */
export function isUserDataReadyForPageView(
    isUserDataLoading: boolean,
    hasSettledUserQuery: boolean,
    userFullProfile: FullUser | undefined | null,
    isQueryError = false,
    hasLoggedInSession = false,
    pageType?: string | null,
): boolean {
    if (isUserDataLoading || !hasSettledUserQuery) {
        return false;
    }

    // Logged-in readiness matches subscriber emission: activeProfile everywhere, or picker-only without profile.
    if (userFullProfile?.isLoggedIn) {
        if (shouldEmitSubscriberTrackingFields(userFullProfile, pageType)) {
            return true;
        }

        return isQueryError;
    }

    // Stale auth cookie must not block a settled logged-out GraphQL payload.
    if (userFullProfile?.isLoggedIn === false) {
        return true;
    }

    if (hasLoggedInSession && !isQueryError) {
        return false;
    }

    // Placeholder / unresolved session: anon-eligible once settled unless cookies indicate login.
    if (!userFullProfile?.isLoggedIn || isQueryError) {
        return true;
    }

    return false;
}

export function isAuthenticatedFullUser(userFullProfile: FullUser | undefined | null): boolean {
    return Boolean(userFullProfile?.isLoggedIn && userFullProfile?.activeProfile?.id);
}

/**
 * Whether page-load tracking should include subscriber fields (userInfo + flat Adobe keys).
 * Preserves PR #4017 app-wide: requires activeProfile unless on a profile-picker route.
 */
export function shouldEmitSubscriberTrackingFields(
    userFullProfile: FullUser | undefined | null,
    pageType?: string | null,
): boolean {
    if (!userFullProfile?.isLoggedIn) {
        return false;
    }

    if (userFullProfile.activeProfile?.id) {
        return true;
    }

    return Boolean(pageType && PROFILE_PICKER_PAGE_TYPES.has(pageType));
}

/**
 * Whether to emit the ANON userInfo shape on page load.
 * Settled isLoggedIn === false wins over a stale auth cookie; cookie still
 * suppresses ANON while the GraphQL payload is unresolved (placeholder).
 */
export function shouldEmitAnonUserInfo(
    userFullProfile: FullUser | undefined | null,
    hasLoggedInSession: boolean,
): boolean {
    if (userFullProfile?.isLoggedIn === false) {
        return true;
    }

    if (userFullProfile?.isLoggedIn) {
        return false;
    }

    return !hasLoggedInSession;
}

/** Key for post-bootstrap utag sync — fingerprint of emitted subscriber flat fields. */
export function getSubscriberUtagSyncKey(
    userFullProfile: FullUser | undefined | null,
    pageType?: string | null,
): string | null {
    if (!shouldEmitSubscriberTrackingFields(userFullProfile, pageType)) {
        return null;
    }

    const fields = buildAdobeUserTrackingFields(userFullProfile);

    return [
        pageType ?? '',
        fields.userRegId,
        fields.userType,
        fields.userStatus,
        fields.userProfileId,
        fields.userProfileRefId,
        fields.userProfileCategory,
        fields.userProfileMaster,
        fields.packageSource,
        fields.packageStatus,
    ].join('|');
}
